import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Typography, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import { TreeItem } from '@mui/x-tree-view';
import type { StatusesByField } from 'business/types';
import FieldStatusChip from 'components/SharedComponents/FieldStatusInformation/FieldStatusChip';
import { translateBlock } from 'i18n';
import type {
    CatalogSelection,
    CatalogTreeField,
} from 'modules/datacatalog/types';

import FieldRenderer from './FieldRenderer';
import StyHighlightableLabel from './StyHighlightableLabel';

type Props = {
    tableName: string;
    fields: CatalogTreeField[];
    domain: string;
    category: string;
    isSelected: boolean;
    selectedFieldName: string | null;
    onSelect: (selection: CatalogSelection) => void;
    searchQuery?: string;
    displayedStatuses?: StatusesByField;
};

const TableRenderer = ({
    tableName,
    fields,
    domain,
    category,
    isSelected,
    selectedFieldName,
    onSelect,
    searchQuery = undefined,
    displayedStatuses,
}: Props) => {
    const theme = useTheme();
    const tableLabel = translateBlock(tableName);

    const isDirectMatch =
        !!searchQuery &&
        tableLabel.toLowerCase().includes(searchQuery.toLowerCase());

    return (
        <TreeItem
            itemId={`${domain}-table-${tableName}`}
            onClick={(e) => e.stopPropagation()}
            label={
                <Stack
                    direction="row"
                    spacing={1}
                    onClick={() =>
                        onSelect({
                            kind: 'table',
                            name: tableName,
                            item: {
                                field: tableName,
                                label: tableLabel,
                                table: tableName,
                                type: 'table',
                            },
                            domain,
                            category,
                            fields,
                        })
                    }
                    sx={{
                        alignItems: 'center',
                        py: 0.5,
                    }}
                >
                    <TableChartOutlinedIcon
                        sx={{
                            color: isSelected
                                ? theme.palette.primary.main
                                : theme.palette.primary.light,
                        }}
                    />
                    <StyHighlightableLabel
                        label={tableLabel}
                        isSelected={isSelected}
                        isDirectMatch={isDirectMatch}
                    />
                    <Typography variant="caption" color="textDisabled">
                        {fields.filter(({ type }) => type !== 'table').length}
                    </Typography>
                    {displayedStatuses && (
                        <FieldStatusChip
                            currentStatus={displayedStatuses[tableName]}
                        />
                    )}
                </Stack>
            }
        >
            {fields.map((f) => (
                <FieldRenderer
                    key={f.field}
                    field={f}
                    domain={domain}
                    category={category}
                    isSelected={selectedFieldName === f.field}
                    onSelect={onSelect}
                    searchQuery={searchQuery}
                    displayedStatuses={displayedStatuses}
                />
            ))}
        </TreeItem>
    );
};

export default TableRenderer;
